import { DataGrid } from "@mui/x-data-grid";
import {
  Paper,
  Typography,
  Divider,
  Box,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Alert,
} from "@mui/material";
import { deployTagsToAssets, queueTagSync } from "../../api/APIHelpers";
import { useEffect, useMemo, useState } from "react";
import { UploadFile } from "@mui/icons-material";
import { useDarkMode } from "../../common/contexts/GlobalContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";
import {
  validateTagIdUniquenessSingleAsset,
  getTagsWithExcessiveParentTiers,
} from "../helpers/Helpers";
import { PreSaveWarningsContent } from "./PreSaveWarningsContent";

const TagDeployer = ({
  tags,
  targets,
  setInfoMessages,
  setConfirmationOnClose,
  setConfirmationText,
  setConfirmationTitle,
  setShowConfirmDeployDialog,
}) => {
  const [deployList, setDeployList] = useState([]);
  const [qTagSync, setQTagSync] = useState(false);
  const [overwriteTags, setOverwriteTags] = useState(false);

  const darkModeState = useDarkMode();

  const excessiveParentTagChains = useMemo(
    () => getTagsWithExcessiveParentTiers(tags),
    [tags]
  );

  useEffect(() => {
    let newDeployList = [];
    let tagIndex = 0;
    let rowId = 1;
    for (const tag of tags) {
      for (const target of targets) {
        let deployObj = {
          id: rowId,
          TagIndex: tagIndex,
          AssetNumber: target.AssetNumber,
          AssetId: target.AssetId,
          TagName: tag.TagName,
          DeployStatus: "Pending",
          ErrorMessage: "",
        };
        newDeployList.push(deployObj);
        rowId += 1;
      }
      tagIndex += 1;
    }
    setDeployList(newDeployList);
  }, [tags, targets]);

  const deploy = async () => {
    try {
      // Deploy tags to the specified assets
      let assetList = targets
        .map((target) => target.AssetNumber.toString())
        .join(", ");
      setConfirmationText(
        <>
          {"Tags in the grid will deploy to the following assets: " + assetList}
          <PreSaveWarningsContent tags={tags} allTags={tags} />
        </>
      );
      setConfirmationTitle("Confirm Deployment");
      const onClose = async (result) => {
        setShowConfirmDeployDialog(false);
        if (result === true) {
          setInfoMessages({
            type: "snackbar",
            severity: "info",
            message:
              "Tags in the grid have been queued for deployment to the specified assets.",
            open: true,
          });
          try {
            validateTagIdUniquenessSingleAsset(tags);
          } catch (err) {
            setInfoMessages({
              type: "snackbar",
              severity: "error",
              message: `Tag deployment failed: ${err.message}`,
              open: true,
              autoHideDuration: 10000,
            });
            return;
          }
          const res = await deployTagsToAssets(
            deployList,
            tags,
            setDeployList,
            overwriteTags
          );
          const success = [];
          const fails = [];
          res.forEach((item) => {
            if (item.status === "rejected") {
              fails.push(item);
            } else {
              success.push(item);
            }
          });

          if (fails.length === 0) {
            setInfoMessages({
              message: "Tags deployed successfully.",
              severity: "success",
              type: "snackbar",
              open: true,
            });
          } else {
            const tagsFailed = fails.map((item) => {
              const reason = item.reason;
              let dataObj = {};
              try {
                dataObj = JSON.parse(reason?.config?.data || "{}");
              } catch {
                dataObj = {};
              }
              return {
                TagName: dataObj.TagName,
                AssetNumber: dataObj.AssetNumber,
                reason: extractApiErrorMessage(reason),
              };
            });
            const failureStrings = tagsFailed.map((item) => {
              return `TagName: ${item.TagName}, AssetNumber: ${item.AssetNumber}, Reason: ${item.reason}`;
            });
            setInfoMessages({
              type: "dialog",
              message: (
                <div>
                  There was an error deploying some of your tags. The following
                  tags did not deploy properly:
                  <br />
                  {failureStrings.map((str) => {
                    return (
                      <span key={str}>
                        {str}
                        <br />
                      </span>
                    );
                  })}
                </div>
              ),
              title: "Deployment Failures",
              open: true,
            });
          }

          if (qTagSync) {
            const assetIds = targets.map((target) => target.AssetId);
            queueTagSync(assetIds);
          }
        }
      };
      setConfirmationOnClose(() => onClose);
      setShowConfirmDeployDialog(true);
    } catch (error) {
      console.error("Failed to deploy tags:", error);
      setInfoMessages({
        type: "snackbar",
        message: `Failed to deploy tags: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
        autoHideDuration: 8000,
      });
    }
  };

  const columnModel = [
    {
      field: "AssetNumber",
      headerName: "AssetNumber",
      flex: 0.5,
      type: "string",
      editable: false,
    },
    {
      field: "TagName",
      headerName: "Tag Name",
      flex: 0.5,
      type: "string",
      editable: false,
    },
    {
      field: "DeployStatus",
      headerName: "Deploy Status",
      flex: 0.5,
      type: "string",
      editable: false,
    },
    {
      field: "ErrorMessage",
      headerName: "Error Message",
      flex: 1,
      type: "string",
      editable: false,
    },
  ];

  return (
    <Paper sx={{ p: 2, my: 1, minHeight: 100 }}>
      <Typography gutterBottom variant='h5' component='div' color='primary'>
        Deploy Tags
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Box>
        <DataGrid
          sx={{
            "--DataGrid-containerBackground": darkModeState
              ? "#3574e3"
              : "#017dba",
          }}
          rows={deployList}
          columns={columnModel}
          columnBuffer={5} // Default # of visible columns
        />
      </Box>
      <Divider sx={{ my: 2 }} />
      {excessiveParentTagChains.length > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          The following tags have more than two tiers of parent tag inheritance,
          which is not supported when deploying. Flatten the parent chain before
          deploying:
          <ul style={{ margin: 0 }}>
            {excessiveParentTagChains.map(({ tag, parent, grandparent }) => (
              <li key={`${tag.TagName}-${tag.TagIdentifier}`}>
                {`${tag.TagName} (${tag.TagIdentifier}) \u2192 ${parent.TagName} (${parent.TagIdentifier}) \u2192 ${grandparent.TagName} (${grandparent.TagIdentifier})`}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Grid container justifyContent={"flex-end"} spacing={2}>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={qTagSync}
                onChange={(e) => {
                  setQTagSync(e.target.checked);
                }}
              />
            }
            label='Queue Tag Sync Immediately'
          />
        </Grid>
        <Grid item>
          <Tooltip title='This will overwrite any Tags with a matching Tag Name and Tag Identifier on the target Assets. If not selected, a new Tag will not be created if there is a matching Tag.'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overwriteTags}
                  onChange={(e) => {
                    setOverwriteTags(e.target.checked);
                  }}
                />
              }
              label='Overwrite Tags'
            />
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip
            title={
              excessiveParentTagChains.length > 0
                ? "Deployment is disabled because one or more tags have more than two tiers of parent tag inheritance. Flatten the parent chain before deploying."
                : ""
            }>
            <span>
              <Button
                onClick={() => {
                  deploy();
                }}
                startIcon={<UploadFile />}
                variant='contained'
                disabled={excessiveParentTagChains.length > 0}>
                Deploy Tags
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TagDeployer;
